import { useState, useEffect } from "react";
import type { ExplorerItem, FileItem, FolderItem } from "@titancloud/shared";
import { formatBytes } from "../../utils/formatBytes";
import { getMimeIcon } from "../../utils/mimeIcons";
import { useExplorerStore } from "../../store/explorerStore";
import { useNavigate } from "react-router-dom";
import { downloadFile } from "../../api/files.api";
import { downloadFolderZip } from "../../api/folders.api";
import { downloadAdminExplorerFile } from "../../api/admin.api";
import { useThumbnailUrl } from "../../hooks/useThumbnailUrl";
import { usePrefetchFolder } from "../../hooks/usePrefetchFolder";

interface FileCardProps {
  item: ExplorerItem;
  onOpen: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  forceRename?: boolean;
  onForceRenameDone?: () => void;
  readOnly?: boolean;
  explorerUserId?: string;
  onFolderNavigate?: (folderId: string) => void;
}

const ICON_COLORS: Record<string, string> = {
  image: "text-pink-400",
  video: "text-purple-400",
  audio: "text-indigo-400",
  pdf: "text-red-400",
  code: "text-green-400",
  archive: "text-yellow-400",
  document: "text-blue-400",
  spreadsheet: "text-emerald-400",
  presentation: "text-orange-400",
  generic: "text-gray-400",
};

export function FileCard({
  item,
  onOpen,
  onRename,
  onDelete,
  forceRename,
  onForceRenameDone,
  readOnly,
  explorerUserId,
  onFolderNavigate,
}: FileCardProps) {
  const { selectedIds, toggleSelect } = useExplorerStore();
  const navigate = useNavigate();
  const isSelected = readOnly ? false : selectedIds.has(item.id);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(item.name);

  useEffect(() => {
    if (forceRename) {
      setRenameValue(item.name);
      setIsRenaming(true);
    }
  }, [forceRename, item.name]);

  const iconType = item.kind === "file"
    ? getMimeIcon((item as FileItem).mimeType, (item as FileItem).extension)
    : "folder";

  // Only image/video files ever have a thumbnail worth showing edge-to-edge.
  const isMedia = iconType === "image" || iconType === "video";
  const fileItem = item.kind === "file" ? (item as FileItem) : null;

  // List/search endpoints already presign thumbnails for every media file, so
  // `thumbnailUrl` is normally present on the item and no request is needed here.
  // The hook only fires as a fallback when a source hasn't enriched the item yet.
  const needsThumbnailFetch = isMedia && fileItem?.thumbnailUrl === undefined;
  const fetchedThumbnailUrl = useThumbnailUrl(item.id, needsThumbnailFetch, explorerUserId);
  const thumbnailUrl = fileItem?.thumbnailUrl ?? fetchedThumbnailUrl;

  const [thumbnailError, setThumbnailError] = useState(false);
  useEffect(() => { setThumbnailError(false); }, [thumbnailUrl]);

  const showAsMediaTile = isMedia && !!thumbnailUrl && !thumbnailError;

  // The admin explorer reads through its own endpoints, so only the user's own folders prefetch.
  const prefetchHandlers = usePrefetchFolder(item.id);
  const folderPrefetch = item.kind === "folder" && !onFolderNavigate && !explorerUserId ? prefetchHandlers : {};

  const handleClick = () => {
    if (isRenaming) return;
    if (item.kind === "folder") {
      if (onFolderNavigate) onFolderNavigate(item.id);
      else navigate(`/app/folder/${item.id}`);
    } else {
      onOpen();
    }
  };

  const finishRename = (save: boolean) => {
    if (save && renameValue.trim()) onRename(renameValue.trim());
    else setRenameValue(item.name);
    setIsRenaming(false);
    onForceRenameDone?.();
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") finishRename(true);
    else if (e.key === "Escape") finishRename(false);
  };

  const downloadIcon = (
    <svg className="h-3 w-3" viewBox="0 0 16 16" fill="currentColor">
      <path d="M.5 9.9a.5.5 0 01.5.5v2.5a1 1 0 001 1h12a1 1 0 001-1v-2.5a.5.5 0 011 0v2.5a2 2 0 01-2 2H2a2 2 0 01-2-2v-2.5a.5.5 0 01.5-.5z" />
      <path d="M7.646 11.854a.5.5 0 00.708 0l3-3a.5.5 0 00-.708-.708L8.5 10.293V1.5a.5.5 0 00-1 0v8.793L5.354 8.146a.5.5 0 10-.708.708l3 3z" />
    </svg>
  );

  // Checkbox — hidden in read-only admin explorer. Shared by both card layouts.
  const checkboxOverlay = !readOnly && (
    <div
      className={`absolute left-2 top-2 z-10 transition-opacity ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
    >
      <div className={`flex h-4 w-4 items-center justify-center rounded border ${
        isSelected ? "border-indigo-500 bg-indigo-500" : "border-gray-600 bg-gray-900"
      }`}>
        {isSelected && (
          <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 8" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    </div>
  );

  // Hover actions (download / rename / delete or admin erase) — shared by both card layouts.
  const hoverActions = !isRenaming && (
    readOnly && explorerUserId && item.kind === "file" ? (
      <div
        className="absolute right-1.5 top-1.5 z-10 hidden gap-1 group-hover:flex"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => void downloadAdminExplorerFile(explorerUserId, item.id, item.name)}
          className="rounded p-1 text-gray-300 hover:bg-black/40 hover:text-blue-400"
          title="Download"
        >
          {downloadIcon}
        </button>
      </div>
    ) : !readOnly && (
      <div
        className="absolute right-1.5 top-1.5 z-10 hidden gap-1 group-hover:flex"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => {
            if (item.kind === "folder") downloadFolderZip(item.id, item.name);
            else void downloadFile(item.id, item.name);
          }}
          className="rounded p-1 text-gray-300 hover:bg-black/40 hover:text-blue-400"
          title={item.kind === "folder" ? "Download as ZIP" : "Download"}
        >
          {downloadIcon}
        </button>
        <button
          onClick={() => setIsRenaming(true)}
          className="rounded p-1 text-gray-300 hover:bg-black/40 hover:text-gray-100"
          title="Rename"
        >
          <svg className="h-3 w-3" viewBox="0 0 16 16" fill="currentColor">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1 text-gray-300 hover:bg-red-900/60 hover:text-red-400"
          title="Delete"
        >
          <svg className="h-3 w-3" viewBox="0 0 16 16" fill="currentColor">
            <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z" />
            <path fillRule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 110-2h3.5l1-1h3l1 1h3.5a1 1 0 011 1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    )
  );

  // Photo/video tile: borderless, edge-to-edge — name and actions live in a hover scrim instead of a padded footer.
  if (showAsMediaTile) {
    return (
      <div
        className={`group relative flex h-full w-full cursor-pointer select-none overflow-hidden rounded-xl bg-gray-800/60 ${
          isSelected ? "ring-2 ring-brand-500" : ""
        }`}
        onClick={handleClick}
      >
        {checkboxOverlay}

        <img
          src={thumbnailUrl!}
          alt={item.name}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          onError={() => setThumbnailError(true)}
        />

        {iconType === "video" && (
          <div className="pointer-events-none absolute bottom-1.5 right-1.5 rounded-full bg-black/60 p-1">
            <svg className="h-3 w-3 text-white" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 3.5a.5.5 0 01.79-.41l6 4.5a.5.5 0 010 .82l-6 4.5A.5.5 0 014 12.5v-9z" />
            </svg>
          </div>
        )}

        {hoverActions}

        <div
          className={`absolute inset-x-0 bottom-0 items-center bg-gradient-to-t from-black/85 to-transparent px-2 pb-1.5 pt-5 ${
            isRenaming ? "flex" : "hidden group-hover:flex"
          }`}
        >
          {isRenaming ? (
            <input
              autoFocus
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={() => finishRename(false)}
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded border border-brand-500 bg-black/70 px-1 py-0.5 text-xs text-gray-100 focus:outline-none"
            />
          ) : (
            <p className="line-clamp-1 w-full break-all text-xs text-white" title={item.name}>
              {item.name}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative flex h-full w-full flex-col items-center gap-2 rounded-xl border p-3 cursor-pointer select-none transition-colors min-w-0 overflow-hidden ${
        isSelected
          ? "border-brand-500 bg-brand-900/20"
          : "border-gray-800 bg-gray-800/40 hover:border-gray-700 hover:bg-gray-800"
      }`}
      onClick={handleClick}
      {...folderPrefetch}
    >
      {checkboxOverlay}

      {/* Preview — fixed height so all cards align regardless of thumbnail vs icon */}
      <div className="relative flex h-20 w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-800/60">
        {item.kind === "folder" ? (
          <svg className="h-10 w-10 text-yellow-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
          </svg>
        ) : (
          <svg
            className={`h-10 w-10 ${ICON_COLORS[iconType] ?? "text-gray-400"}`}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.625 1.5H9a3.75 3.75 0 013.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 013.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 01-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875zm6.905 9.97a.75.75 0 00-1.06 0l-3 3a.75.75 0 101.06 1.06l1.72-1.72V18a.75.75 0 001.5 0v-4.19l1.72 1.72a.75.75 0 101.06-1.06l-3-3z"
              clipRule="evenodd"
            />
            <path d="M14.25 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0016.5 7.5h-1.875a.375.375 0 01-.375-.375V5.25z" />
          </svg>
        )}
      </div>

      {/* Name — fixed 2-line height so size label is always at the same position */}
      <div className="flex h-8 w-full items-center justify-center">
        {isRenaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={() => finishRename(false)}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded border border-brand-500 bg-gray-900 px-1 py-0.5 text-center text-xs text-gray-100 focus:outline-none"
          />
        ) : (
          <p
            className="line-clamp-2 w-full break-all text-center text-xs leading-tight text-gray-300 group-hover:text-white"
            title={item.name}
          >
            {item.name}
          </p>
        )}
      </div>

      {/* Size / item count — always at the same vertical position */}
      {item.kind === "file" ? (
        <p className="text-[10px] text-gray-600">{formatBytes((item as FileItem).sizeBytes)}</p>
      ) : (
        <p className="text-[10px] text-gray-600">
          {(item as FolderItem).childCount} item{(item as FolderItem).childCount !== 1 ? "s" : ""}
        </p>
      )}

      {hoverActions}
    </div>
  );
}
